import { Const } from "../core/constants";
import type { CanvasModel } from "../core/canvasModel";
import type { KarelModelShape } from "../core/karelModel";
import { karelImages } from "./karelImages";

export function getCornerX(canvasModel: CanvasModel, col: number): number {
  return canvasModel.getWorldLeft() + col * canvasModel.getCornerSize();
}

export function getCornerY(canvasModel: CanvasModel, row: number): number {
  return canvasModel.getWorldTop() + row * canvasModel.getCornerSize();
}

// At small cell sizes the detailed robot is replaced by a direction arrow.
// Both sprites are authored facing East, so the other directions are drawn by
// rotating the image around the cell centre.
function getKarelImage(cornerSize: number): HTMLImageElement | undefined {
  if (cornerSize <= Const.KAREL_SMALL_SIZE) return karelImages.karelArrow;
  return karelImages.karel;
}

function getDirectionAngle(direction: number): number {
  switch (direction) {
    case Const.KAREL_NORTH:
      return -Math.PI / 2;
    case Const.KAREL_SOUTH:
      return Math.PI / 2;
    case Const.KAREL_WEST:
      return Math.PI;
    default:
      return 0;
  }
}

function drawKarel(
  canvasModel: CanvasModel,
  karelModel: KarelModelShape,
  c: CanvasRenderingContext2D,
): void {
  const cornerSize = canvasModel.getCornerSize();
  const image = getKarelImage(cornerSize);
  if (image == null) {
    alert("karel image loading fail!");
    return;
  }
  const karelX = getCornerX(canvasModel, karelModel.getKarelCol());
  const karelY = getCornerY(canvasModel, karelModel.getKarelRow());
  const size = cornerSize - 2;
  c.save();
  c.translate(karelX + 1 + size / 2, karelY + 1 + size / 2);
  c.rotate(getDirectionAngle(karelModel.getDirection()));
  c.drawImage(image, -size / 2, -size / 2, size, size);
  c.restore();
}

function drawBorder(
  canvasModel: CanvasModel,
  c: CanvasRenderingContext2D,
): void {
  const borderLeft = canvasModel.getWorldLeft() - Const.BORDER_SIZE;
  const borderTop = canvasModel.getWorldTop() - Const.BORDER_SIZE;
  const borderWidth = canvasModel.getWorldWidth() + Const.BORDER_SIZE * 2;
  const borderHeight = canvasModel.getWorldHeight() + Const.BORDER_SIZE * 2;
  c.fillStyle = Const.CANVAS_WALL_COLOR;
  c.fillRect(borderLeft, borderTop, borderWidth, borderHeight);

  c.fillStyle = Const.BACKGROUND_COLOR;
  c.fillRect(
    canvasModel.getWorldLeft(),
    canvasModel.getWorldTop(),
    canvasModel.getWorldWidth(),
    canvasModel.getWorldHeight(),
  );
}

function drawBeepers(
  canvasModel: CanvasModel,
  karelModel: KarelModelShape,
  c: CanvasRenderingContext2D,
): void {
  const beeperSize = canvasModel.getCornerSize() * Const.BEEPER_SIZE_FRACTION;
  const cornerSize = canvasModel.getCornerSize();

  c.fillStyle = Const.CANVAS_TEXT_COLOR;
  c.font = "bold 14px courier";
  c.textAlign = "center";
  c.textBaseline = "middle";

  for (let rIndex = 0; rIndex < karelModel.getNumRows(); rIndex++) {
    for (let cIndex = 0; cIndex < karelModel.getNumCols(); cIndex++) {
      const x = getCornerX(canvasModel, cIndex) + cornerSize / 2;
      const y = getCornerY(canvasModel, rIndex) + cornerSize / 2;
      const numBeepers = karelModel.getNumBeepers(rIndex, cIndex);
      if (numBeepers > 0) {
        const label = numBeepers > 1 ? String(numBeepers) : "";
        drawBeeper(c, x, y, beeperSize, label);
      }
    }
  }
}

function drawBeeper(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  sqSize: number,
  label: string,
): void {
  const beeperSize = sqSize * Const.BEEPER_SIZE_FRACTION;
  let ps = Math.round(sqSize * Const.BEEPER_LABEL_FRACTION);
  ps = Math.min(ps, Const.BEEPER_LABEL_MAX_SIZE);
  if (ps < Const.BEEPER_LABEL_MIN_SIZE) ps = 0;
  ctx.save();
  ctx.fillStyle = Const.BEEPER_FILL_COLOR;
  ctx.beginPath();
  ctx.moveTo(x - beeperSize / 2, y);
  ctx.lineTo(x, y + beeperSize / 2);
  ctx.lineTo(x + beeperSize / 2, y);
  ctx.lineTo(x, y - beeperSize / 2);
  ctx.lineTo(x - beeperSize / 2, y);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
  ctx.save();
  if (ps && label) {
    ctx.fillStyle = Const.CANVAS_TEXT_COLOR;
    ctx.font = ps + "pt " + Const.BEEPER_LABEL_FONT;
    const y0 = y + ps * Const.BEEPER_LABEL_DY;
    ctx.fillText(label, x, y0);
  }
  ctx.restore();
}

function drawCorners(
  canvasModel: CanvasModel,
  karelModel: KarelModelShape,
  c: CanvasRenderingContext2D,
): void {
  const crossSize = canvasModel.getCornerSize() * Const.CROSS_FRACTION;
  const cornerSize = canvasModel.getCornerSize();

  for (let rIndex = 0; rIndex < karelModel.getNumRows(); rIndex++) {
    for (let cIndex = 0; cIndex < karelModel.getNumCols(); cIndex++) {
      const squareLeft = getCornerX(canvasModel, cIndex);
      const squareTop = getCornerY(canvasModel, rIndex);
      const squareColor = karelModel.getSquareColor(rIndex, cIndex);

      if (squareColor != null) {
        c.fillStyle = squareColor;
        c.fillRect(squareLeft, squareTop, cornerSize, cornerSize);
      } else if (crossSize > Const.MIN_CROSS_SIZE) {
        // Draw the intersection cross with canvas primitives so its color
        // follows the active theme instead of a baked-in sprite.
        const x = squareLeft + (cornerSize - crossSize) / 2;
        const y = squareTop + (cornerSize - crossSize) / 2;
        const thickness = Math.max(
          1,
          crossSize * Const.CROSS_THICKNESS_FRACTION,
        );
        c.fillStyle = Const.CANVAS_GRID_COLOR;
        c.fillRect(x + (crossSize - thickness) / 2, y, thickness, crossSize);
        c.fillRect(x, y + (crossSize - thickness) / 2, crossSize, thickness);
      } else {
        const x = squareLeft + cornerSize / 2;
        const y = squareTop + cornerSize / 2;
        const size = Math.max(1, crossSize / 2);
        c.beginPath();
        c.arc(x, y, size, 0, Math.PI * 2, true);
        c.closePath();
        c.fillStyle = Const.CANVAS_GRID_COLOR;
        c.fill();
      }
    }
  }
}

function drawBackground(
  canvasModel: CanvasModel,
  karelModel: KarelModelShape,
  c: CanvasRenderingContext2D,
): void {
  drawBorder(canvasModel, c);
  drawCorners(canvasModel, karelModel, c);
  drawBeepers(canvasModel, karelModel, c);
}

function drawWalls(
  canvasModel: CanvasModel,
  karelModel: KarelModelShape,
  c: CanvasRenderingContext2D,
): void {
  const cornerSize = canvasModel.getCornerSize();
  let wallThickness = cornerSize * Const.WALL_THICKNESS_FRACTION;
  if (wallThickness < 2) wallThickness = 2;
  if (wallThickness > 3) wallThickness = 3;
  c.fillStyle = Const.CANVAS_WALL_COLOR;

  for (let rIndex = 0; rIndex < karelModel.getNumRows(); rIndex++) {
    for (let cIndex = 0; cIndex < karelModel.getNumCols(); cIndex++) {
      if (karelModel.hasTopWall(rIndex, cIndex)) {
        const x = getCornerX(canvasModel, cIndex) - wallThickness / 2;
        const y = getCornerY(canvasModel, rIndex) - wallThickness / 2;
        c.fillRect(x, y, cornerSize + wallThickness, wallThickness);
      }
      if (karelModel.hasRightWall(rIndex, cIndex)) {
        const x = getCornerX(canvasModel, cIndex + 1) - wallThickness / 2;
        const y = getCornerY(canvasModel, rIndex) - wallThickness / 2;
        c.fillRect(x, y, wallThickness, cornerSize + wallThickness);
      }
    }
  }
}

export const KarelView = {
  getCornerX,
  getCornerY,
  drawKarel,
  drawBorder,
  drawBeepers,
  drawBeeper,
  drawCorners,
  drawBackground,
  drawWalls,
  draw(
    canvasModel: CanvasModel,
    karelModel: KarelModelShape,
    c: CanvasRenderingContext2D,
  ): void {
    drawBackground(canvasModel, karelModel, c);
    drawKarel(canvasModel, karelModel, c);
    drawWalls(canvasModel, karelModel, c);
  },
};
